'use client';

/**
 * THE UNREAD COUNT, FOR CHROME THAT IS NOT THE RAIL (2026-10-03).
 *
 * WHY. The unread badge rides the account avatar: in the rail on a desktop,
 * in the bottom bar on a phone, and Notifications is the first row of the
 * panel the avatar opens. Full screen hides both (the rail draws no column,
 * the bottom bar steps aside), so an ordinary notification that arrived
 * while an app was full screen could not be seen until the person left it.
 * Only the ones that need an answer were on screen there, as the toast that
 * components/critical-notification-overlay.tsx draws over every page. The
 * owner, looking at it: "how do users see notifications in fullscreen mode?
 * add a badge + menuitem to the ... menu?". The app bar is the one piece of
 * the platform left on a full-screen app, so its ⋯ carries the badge and its
 * menu carries a Notifications row (components/workspace/nodes/node-frame.tsx).
 *
 * WHY A MIRROR AND NOT THE HOOK. `useNotifications` keeps its own state and
 * makes its own two requests per caller, and every window on the canvas
 * mounts a frame that could draw a bar: calling the hook there would ask the
 * server once per open window. The rail is mounted once per workspace page
 * in every layout (its full-screen and phone branches draw no column but
 * still run its hooks) and already holds the count for its own badge. It
 * publishes that number here and the bars read it: one reader of the
 * server, one number, so the bar's badge cannot disagree with the rail's.
 *
 * WHAT THE VALUE MEANS. `null`: nobody to notify. Signed out, or no rail on
 * the page (the standalone `/w/` page and an app's own address draw the same
 * bar and must get no row: they have no notifications panel to open). `0`: a
 * signed-in person with nothing unread, so the row stays and the badge goes.
 */

import { useSyncExternalStore } from 'react';

let current: number | null = null;
const listeners = new Set<() => void>();

/** The rail's own count, or `null` when there is no one to notify (see the file note). */
export function publishUnreadNotifications(next: number | null): void {
  // A count that is not a whole number above zero is zero: a badge reading
  // "-1" or "NaN" is worse than no badge.
  const value = next === null ? null : Number.isFinite(next) && next > 0 ? Math.floor(next) : 0;
  if (value === current) return;
  current = value;
  for (const listener of [...listeners]) listener();
}

export function getUnreadNotifications(): number | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The server renders no bar's badge: the count is the browser's. */
const serverSnapshot = (): number | null => null;

export function useUnreadNotifications(): number | null {
  return useSyncExternalStore(subscribe, getUnreadNotifications, serverSnapshot);
}

/**
 * The chip's text. `cap` is the largest number written out: 99 where there
 * is room (the rail's avatar, a menu row), 9 on a small target (the phone's
 * bottom bar, the ⋯ tile), the two caps the existing badges already use.
 */
export function unreadBadgeText(count: number, cap: 9 | 99 = 99): string {
  return count > cap ? `${cap}+` : String(count);
}
